import React, { useState, useRef, useEffect } from "react";
import { LogOut, User } from "lucide-react";
import { useApp } from "../state/AppContext";

// Subtle top-right avatar shown across public app screens once a participant
// has signed up. Opens a small popover with sign-out. Admin & Parent screens
// have their own headers and are unaffected by this control.
export default function UserMenuButton() {
  const { user, resetUser, t } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!user) return null;

  const first = (user.first_name || "").trim();
  const last = (user.last_name || "").trim();
  const initials = ((first[0] || "") + (last[0] || "")).toUpperCase() || (user.email || "?").slice(0, 1).toUpperCase();

  const doSignOut = () => {
    setOpen(false);
    resetUser();
  };

  return (
    <div className="absolute top-3 right-3 z-30" ref={ref} data-testid="user-menu-root">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="user-menu-avatar"
        aria-label="Account menu"
        className="w-10 h-10 rounded-full bg-white shadow-warm-sm border border-scl-border flex items-center justify-center text-scl-charcoal text-sm font-bold hover:bg-scl-cream active:scale-95 transition-all"
      >
        {initials}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-warm border border-scl-border overflow-hidden" data-testid="user-menu-popover">
          <div className="px-4 py-3 border-b border-scl-border/60">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-scl-charcoal/50 font-semibold mb-1">
              <User className="w-3 h-3" /> Signed in
            </div>
            <div className="text-sm font-semibold text-scl-charcoal truncate">{first} {last}</div>
            <div className="text-[11px] text-scl-charcoal/55 truncate">{user.email}</div>
            {typeof user.points === "number" && (
              <div className="mt-1.5 inline-block px-2 py-0.5 rounded-full bg-scl-mustard text-scl-charcoal text-[10px] font-bold uppercase tracking-widest">
                {user.points} pts
              </div>
            )}
          </div>
          <button
            onClick={doSignOut}
            data-testid="user-menu-signout"
            className="w-full min-h-[44px] px-4 py-3 flex items-center gap-2 text-sm text-scl-charcoal hover:bg-scl-cream text-left"
          >
            <LogOut className="w-4 h-4 text-scl-brick" />
            <span>{t("nav_signout") || "Sign out"}</span>
          </button>
        </div>
      )}
    </div>
  );
}
